import './appInsights';
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import ErrorBoundary from './components/ErrorBoundary';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { msalConfig } from './authConfig';

const msalInstance = new PublicClientApplication(msalConfig);
const root = ReactDOM.createRoot(document.getElementById('root'));

async function startApp() {
  // Clear any stale redirect flag left by a previous implementation
  sessionStorage.removeItem('msal_redirect_in_progress');

  try {
    await msalInstance.initialize();

    let result = null;
    try {
      result = await msalInstance.handleRedirectPromise();
    } catch (redirectErr) {
      // A stale or mismatched auth response (e.g. no_token_request_cache_error) must NOT
      // brick the app. Reset the broken auth state, strip the leftover response hash from
      // the URL, and fall through to a clean load so the user can sign in again.
      console.error('handleRedirectPromise failed; resetting auth state:', redirectErr);
      try {
        await msalInstance.clearCache();
      } catch (_) { /* ignore */ }
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      result = null;
    }

    if (result) {
      msalInstance.setActiveAccount(result.account);
    } else {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        // Filter to the configured tenant to avoid stale accounts from other app
        // registrations sitting in localStorage from a previous session.
        const tenantId = process.env.REACT_APP_ENTRA_TENANT_ID;
        const match = tenantId
          ? accounts.find(a => a.tenantId === tenantId || a.homeAccountId?.includes(tenantId))
          : null;
        msalInstance.setActiveAccount(match ?? accounts[0]);
      }
    }

    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
        msalInstance.setActiveAccount(event.payload.account);
      }
    });

    root.render(
      <ErrorBoundary>
        <MsalProvider instance={msalInstance}>
          <App />
        </MsalProvider>
      </ErrorBoundary>
    );
  } catch (err) {
    console.error('MSAL init failed:', err);
    document.getElementById('root').innerHTML =
      `<div style="padding:20px;color:red;font-family:sans-serif"><b>App failed to start:</b><br/>${err.message}</div>`;
  }
}

startApp();

reportWebVitals();
